import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { RootState } from '../store'
import { entitlementsApi, TenantFeature } from '../services/api'

/**
 * Which gated features the current tenant has active.
 *
 * Several surfaces need this at once (left nav, analysis tabs), so the
 * in-flight request is shared per tenant rather than fetched once per
 * consumer. Without that, mounting the layout and a page together issues
 * duplicate calls on every navigation.
 *
 * Defaults to "not entitled" while loading and on failure, matching the
 * backend's fail-closed posture — a UI that flashes a premium tab before
 * hiding it is worse than one that reveals it a beat late. The backend
 * gates the endpoints regardless; this only controls what is shown.
 */

type FeatureMap = Record<string, boolean>

const cache = new Map<string, { at: number; value: FeatureMap }>()
const inflight = new Map<string, Promise<FeatureMap>>()

const TTL_MS = 60_000

export const clearEntitlementsCache = () => {
  cache.clear()
  inflight.clear()
}

const fetchEntitlements = (tenantId: string): Promise<FeatureMap> => {
  const hit = cache.get(tenantId)
  if (hit && Date.now() - hit.at < TTL_MS) return Promise.resolve(hit.value)

  const existing = inflight.get(tenantId)
  if (existing) return existing

  const p = entitlementsApi
    .get(tenantId)
    .then((resp) => {
      const map: FeatureMap = {}
      ;(resp.data.features || []).forEach((f: TenantFeature) => {
        map[f.key] = f.enabled
      })
      cache.set(tenantId, { at: Date.now(), value: map })
      return map
    })
    .catch(() => ({} as FeatureMap)) // fail closed
    .finally(() => {
      inflight.delete(tenantId)
    })

  inflight.set(tenantId, p)
  return p
}

export interface UseEntitlementsResult {
  features: FeatureMap
  has: (key: string) => boolean
  loading: boolean
}

export const useEntitlements = (): UseEntitlementsResult => {
  const { user } = useSelector((state: RootState) => state.auth)
  const tenantId = user?.tenant_id || ''
  const [features, setFeatures] = useState<FeatureMap>({})
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let active = true
    if (!tenantId) {
      setLoading(false)
      return
    }
    fetchEntitlements(tenantId).then((map) => {
      if (!active) return
      setFeatures(map)
      setLoading(false)
    })
    return () => {
      active = false
    }
  }, [tenantId])

  return {
    features,
    has: (key: string) => features[key] === true,
    loading,
  }
}

export default useEntitlements
